import PropTypes from 'prop-types';
// BASE
import React, { useState, useMemo } from 'react';

import { accountsSelectors } from 'app-components/flux/accounts';
// DATA
import { useSelector } from 'react-redux';

// CUSTOM
import SimplifiMultiSelect from 'components/SimplifiSelects/SimplifiMultiSelect';

/* Component */
const AccountMultiSelect = (props) => {
  const { onSelected, name, selectedItems, showAllItemsMenu, accountsSelector, ...other } = props;

  const accounts = useSelector(accountsSelector);

  //  Decides whether you show the 'All' option or not
  const [showAll, setShowAll] = useState(showAllItemsMenu);

  /*
   * Selected items array from ID array
   * */
  const selectedAccounts = useMemo(() => selectedItems.map((id) => accounts.get(id)), [accounts, selectedItems]);

  /*
   * Creates the main array of menu items
   * */
  const menuArray = useMemo(() => {
    // first, sort accounts alphabetically
    const accountsList = accounts
      .toIndexedSeq()
      .toArray()
      .sort((account1, account2) =>
        (account1.name || '').localeCompare(account2.name || '', undefined, {
          numeric: true,
          sensitivity: 'base',
        }),
      );

    // second, group list by account type
    const organizedList = {
      checking: [],
      credit: [],
      savings: [],
      brokerage: [],
      retirement: [],
      otherInvestment: [],
      asset: [],
      loan: [],
      other: [],
    };

    accountsList.forEach((account) => {
      switch (account.type) {
        case 'BANK':
        case 'CASH':
          if (account.subType === 'SAVINGS' || account.subType === 'CD' || account.subType === 'MONEY_MARKET') {
            organizedList.savings.push(account);
          } else {
            organizedList.checking.push(account);
          }
          break;
        case 'CREDIT':
          organizedList.credit.push(account);
          break;
        case 'LOAN':
        case 'OTHER_LIABILITY':
          organizedList.loan.push(account);
          break;
        case 'INVESTMENT':
          if (
            account.subType === null ||
            account.subType === '529' ||
            account.subType === 'UGMA_UTMA' ||
            account.subType === 'OTHER_INVESTMENTS'
          ) {
            organizedList.otherInvestment.push(account);
          } else if (account.subType === 'BROKERAGE') {
            organizedList.brokerage.push(account);
          } else {
            organizedList.retirement.push(account);
          }
          break;
        case 'OTHER_ASSET':
        case 'REAL_ESTATE':
        case 'VEHICLE':
          organizedList.asset.push(account);
          break;
        default:
          organizedList.other.push(account);
      }
    });

    //  If only ONE of the header options has accounts in them, do not show the 'All' option since
    //  that would be redundant, even if showAllItemsMenu is set to true
    if (Object.values(organizedList).filter((items) => items.length).length === 1) {
      setShowAll(false);
    }

    const finalList = [];
    Object.keys(organizedList).forEach((key) => {
      const typeSpecificArray = organizedList[key];
      // only push headers if they have accounts
      if (typeSpecificArray.length) {
        if (key === 'checking') {
          finalList.push({
            name: 'Cash & Checking',
            items: typeSpecificArray,
          });
        } else if (key === 'otherInvestment') {
          finalList.push({
            name: 'Other Investment',
            items: typeSpecificArray,
          });
        } else {
          // default menu item (no key->name mapping needed)
          finalList.push({
            name: key,
            items: typeSpecificArray,
          });
        }
      }
    });

    return finalList;
  }, [accounts]);

  return (
    <SimplifiMultiSelect
      sharedcomponentid='ACCOUNT_MULTI_SELECT'
      menuArray={menuArray}
      onSelected={onSelected}
      name={name}
      selectedItems={selectedAccounts}
      showAll={showAll}
      {...other}
    />
  );
};

AccountMultiSelect.defaultProps = {
  name: 'Accounts',
  showAllItemsMenu: true,
  accountsSelector: accountsSelectors.getTransactionAccounts,
};

AccountMultiSelect.propTypes = {
  onSelected: PropTypes.func.isRequired, // returns array of all selected accounts
  name: PropTypes.string.isRequired,
  selectedItems: PropTypes.array, // array of accountRecords
  showAllItemsMenu: PropTypes.bool, // 'all accounts' super option
  accountsSelector: PropTypes.func, // used to generate the accountList
  id: PropTypes.string,
  className: PropTypes.string,
};

export default AccountMultiSelect;
